import store from "../store/index";
import tenant from "./tenant";
import { recordProblem } from "./problem-log";

const RETRYING = Symbol("retrying");

const http = {
  fetch(
    url,
    data = null,
    successBadge = false,
    method = null,
    jsonDecode = true,
    attempt = 0
  ) {
    if (store.state.logged === undefined && store.state.token === undefined) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          this.fetch(url, data, successBadge, method, jsonDecode)
            .then((data) => {
              resolve(data);
            })
            .catch((err) => {
              reject(err);
            });
        }, 1000);
      });
    }
    return new Promise((resolve, reject) => {
      let obj = {
        headers: {
          Accept: "application/json",
          Authorization: "Bearer " + store.state.token,
        },
      };
      if (data !== null) {
        let body = new FormData();

        for (const key in data) {
          if (typeof data[key] === "object" && !(data[key] instanceof File)) {
            if (key === "tags" && data[key]?.length === 0) {
              body.append(`${key}`, null);
            }
            for (const key2 in data[key]) {
              if (typeof data[key][key2] === "object") {
                for (const key3 in data[key][key2]) {
                  if (typeof data[key][key2][key3] === "object") {
                    for (const key4 in data[key][key2][key3]) {
                      body.append(
                        `${key}[${key2}][${key3}][${key4}]`,
                        data[key][key2][key3][key4]
                      );
                    }
                  } else {
                    body.append(
                      `${key}[${key2}][${key3}]`,
                      data[key][key2][key3]
                    );
                  }
                }
              } else {
                body.append(`${key}[${key2}]`, data[key][key2]);
              }
            }
          } else {
            body.append(key, data[key]);
          }
        }

        obj.method = "POST";
        if (method !== null) {
          body.append("_method", method);
        }
        obj.body = body;
      }
      fetch(tenant.getApiBaseUrl() + url, obj)
        .then((res) => {
          if (res.status === 429 && attempt < 4) {
            // rate limited — back off and retry (the API window can be up
            // to a minute long)
            setTimeout(() => {
              this.fetch(
                url,
                data,
                successBadge,
                method,
                jsonDecode,
                attempt + 1
              )
                .then(resolve)
                .catch(reject);
            }, 4000 * (attempt + 1));
            return RETRYING;
          }
          // Keyed off the status, not the message text: the API does not
          // always answer an expired token with "Unauthenticated.", and when
          // it did not, every request failed in silence — which is how a
          // stalled screen ended up looking like a slow one.
          if (res.status === 401) {
            store.commit("logout");
            window.dispatchEvent(new Event("editor-auth-expired"));
          }
          if (!jsonDecode) {
            return res.text();
          } else {
            if (!res.ok) {
              const error = new Error(res.statusText);
              error.json = res.json();
              throw error;
            }
            return res.json();
          }
        })
        .then((data) => {
          if (data === RETRYING) {
            return;
          }
          if (successBadge) {
            store.commit("setApiSuccess", data);
          }
          resolve(data);
        })
        .catch((err) => {
          if (!err.json || typeof err.json.then !== "function") {
            // network error / CORS failure — there is no response body
            recordProblem(
              "api",
              `Request failed: ${url}`,
              err?.message ?? String(err)
            );
            reject(err);
            return;
          }
          err.json.then((data) => {
            // No hard redirect to the manager: that threw the user out of
            // the editor and took any unsaved work with it. The app shows
            // its own sign-in screen and stays where it was.
            if (data.message === "Unauthenticated.") {
              store.commit("logout");
              window.dispatchEvent(new Event("editor-auth-expired"));
            } else {
              // A refused request is the most common cause of "it did
              // nothing": the caller often has no error branch, so this is
              // the only place it is ever written down. An expired session is
              // left out — that one is normal and already handled above.
              recordProblem(
                "api",
                `${url} — ${data.message ?? "refused"}`,
                data.errors ? JSON.stringify(data.errors) : ""
              );
              if (data.message === "Forbidden." || successBadge) {
                store.commit("setApiError", data);
              }
            }
            reject(data);
          });
        });
    });
  },
};

export default http;
